import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  writeBatch,
  increment,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { db, storage, auth } from './firebase';
import {
  Student,
  StudentRegistration,
  Teacher,
  Notice,
  FeeStructure,
  FeeCategory,
  StudentFee,
  Payment,
  SalaryPayment,
  DashboardStats,
  StudentFormData,
  StudentRegistrationFormData,
  StudentAdmissionFormData,
  TeacherFormData,
  SchoolRegistration,
  SchoolRegistrationFormData,
  AdminUser,
  NoticeFormData,
  FeePaymentFormData,
  FeeStructureFormData,
  SchoolClass,
  ClassFormData,
  Subject,
  SubjectFormData,
  Department,
  DepartmentFormData,
  AttendanceRecord,
  ExamResult
} from '../types';

/**
 * The Indian school academic year runs April–March, so a plain calendar year
 * is wrong for 3 months of every year (Jan–Mar belong to the *previous*
 * session). Returns e.g. "2026-2027" for any date from Apr 2026 to Mar 2027.
 */
export const getCurrentAcademicYear = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const isBeforeApril = now.getMonth() < 3; // Jan(0)-Mar(2)
  return isBeforeApril ? `${year - 1}-${year}` : `${year}-${year + 1}`;
};

// Student Registration API - moved to schoolDataApi.registrations

// Legacy Registration API - now points to schoolDataApi.registrations
export const registrationApi = {
  async create(data: StudentRegistrationFormData): Promise<StudentRegistration> {
    return schoolDataApi.registrations.create(data);
  },
  async getAll(): Promise<StudentRegistration[]> {
    return schoolDataApi.registrations.getAll();
  },
  async getById(id: string): Promise<StudentRegistration | null> {
    return schoolDataApi.registrations.getById(id);
  },
  async approve(id: string, approvedBy: string): Promise<void> {
    return schoolDataApi.registrations.approve(id, approvedBy);
  },
  async reject(id: string, rejectionReason: string): Promise<void> {
    return schoolDataApi.registrations.reject(id, rejectionReason);
  },
  async delete(id: string): Promise<void> {
    return schoolDataApi.registrations.delete(id);
  }
};

// Students API - now points to schoolDataApi.students
export const studentsApi = {
  async createFromRegistration(
    registrationId: string,
    registrationData: StudentRegistration,
    admissionData: StudentAdmissionFormData
  ): Promise<Student> {
    const currentSchoolId = await schoolDataApi.getCurrentSchoolId();
    if (!currentSchoolId) throw new Error('No school context found');

    const studentId = `STU${Date.now()}`;

    const uploadDoc = async (file: File | undefined): Promise<string> => {
      if (!file) return '';
      const fileRef = ref(storage, `students/${studentId}/${file.name}`);
      await uploadBytes(fileRef, file);
      return getDownloadURL(fileRef);
    };

    const [
      photoURL,
      aadharCardURL,
      birthCertificateURL,
      transferCertificateURL,
      marksheetURL,
      medicalCertificateURL,
      casteCertificateURL,
      incomeCertificateURL
    ] = await Promise.all([
      uploadDoc(admissionData.photo),
      uploadDoc(admissionData.aadharCard),
      uploadDoc(admissionData.birthCertificate),
      uploadDoc(admissionData.transferCertificate),
      uploadDoc(admissionData.marksheet),
      uploadDoc(admissionData.medicalCertificate),
      uploadDoc(admissionData.casteCertificate),
      uploadDoc(admissionData.incomeCertificate)
    ]);

    const studentData = {
      studentId,
      registrationId,
      name: registrationData.name,
      dateOfBirth: registrationData.dateOfBirth,
      gender: registrationData.gender,
      class: admissionData.class,
      parentName: registrationData.parentName,
      parentContact: registrationData.parentContact,
      parentEmail: registrationData.parentEmail,
      permanentAddress: admissionData.permanentAddress,
      currentAddress: admissionData.currentAddress,
      aadharNumber: admissionData.aadharNumber,
      motherName: admissionData.motherName,
      fatherName: admissionData.fatherName,
      fatherOccupation: admissionData.fatherOccupation || '',
      motherOccupation: admissionData.motherOccupation || '',
      caste: admissionData.caste || '',
      category: admissionData.category || 'General',
      religion: admissionData.religion || '',
      penNumber: admissionData.penNumber || '',
      apaarId: admissionData.apaarId || '',
      isRteQuota: admissionData.isRteQuota || false,
      annualIncome: admissionData.annualIncome || 0,
      bankAccountNumber: admissionData.bankAccountNumber || '',
      bankName: admissionData.bankName || '',
      ifscCode: admissionData.ifscCode || '',
      accountHolderName: admissionData.accountHolderName || '',
      needsHostel: admissionData.needsHostel || false,
      needsTransport: admissionData.needsTransport || false,
      previousSchool: admissionData.previousSchool || '',
      medicalConditions: admissionData.medicalConditions || '',
      emergencyContact: admissionData.emergencyContact || '',
      photoURL,
      aadharCardURL,
      birthCertificateURL,
      transferCertificateURL,
      marksheetURL,
      medicalCertificateURL,
      casteCertificateURL,
      incomeCertificateURL,
      admissionFee: admissionData.feeAmount || 0,
      admissionStatus: 'Submitted' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'students'), studentData);
    return { id: docRef.id, ...studentData };
  },

  async getAll(): Promise<Student[]> {
    return schoolDataApi.students.getAll();
  },

  async getById(id: string): Promise<Student | null> {
    return schoolDataApi.students.getById(id);
  },

  async update(id: string, data: Partial<Student>): Promise<void> {
    return schoolDataApi.students.update(id, data);
  },

  async delete(id: string): Promise<void> {
    return schoolDataApi.students.delete(id);
  },

  async approveAdmission(id: string, classAssigned: string, section?: string): Promise<void> {
    await schoolDataApi.students.update(id, {
      admissionStatus: 'Approved',
      class: classAssigned,
      ...(section ? { section } : {}),
      admissionDate: new Date().toISOString()
    });
    try {
      await schoolDataApi.studentFees.syncMonthlyFeeForStudent(id, classAssigned);
    } catch (error) {
      console.error('Error syncing monthly fee for student:', error);
    }
  },

  async rejectAdmission(id: string): Promise<void> {
    await schoolDataApi.students.update(id, {
      admissionStatus: 'Rejected'
    });
  },

  // Legacy methods for backward compatibility
  async approve(id: string, classAssigned: string, section?: string): Promise<void> {
    return this.approveAdmission(id, classAssigned, section);
  },

  async reject(id: string): Promise<void> {
    return this.rejectAdmission(id);
  }
};

// Teachers API
export const teachersApi = {
  async create(data: TeacherFormData): Promise<Teacher> {
    const teacherId = `TCH${Date.now()}`;
    let photoURL = '';

    if (data.photo) {
      const photoRef = ref(storage, `teachers/${teacherId}/photo`);
      await uploadBytes(photoRef, data.photo);
      photoURL = await getDownloadURL(photoRef);
    }

    // Upload documents
    const documents: { id: string; name: string; type: string; url: string; uploadedAt: string; }[] = [];
    if (data.documents && data.documents.length > 0) {
      for (let i = 0; i < data.documents.length; i++) {
        const file = data.documents[i];
        const docId = `${teacherId}_doc_${i}_${Date.now()}`;
        const docRef = ref(storage, `teachers/${teacherId}/documents/${docId}`);
        await uploadBytes(docRef, file);
        const docURL = await getDownloadURL(docRef);
        documents.push({
          id: docId,
          name: file.name,
          type: file.type,
          url: docURL,
          uploadedAt: new Date().toISOString()
        });
      }
    }

    const teacherData = {
      teacherId,
      name: data.name,
      email: data.email,
      phone: data.phone,
      aadharNumber: data.aadharNumber,
      subject: data.subject,
      department: data.department || '',
      qualification: data.qualification,
      dateOfJoining: data.dateOfJoining,
      salary: data.salary,
      photoURL,
      documents,
      bankDetails: data.bankDetails || null,
      status: 'Active' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'teachers'), teacherData);
    return { id: docRef.id, ...teacherData };
  },

  async getAll(): Promise<Teacher[]> {
    return schoolDataApi.teachers.getAll();
  },

  async getById(id: string): Promise<Teacher | null> {
    return schoolDataApi.teachers.getById(id);
  },

  async update(id: string, data: TeacherFormData): Promise<Teacher> {
    const existingTeacher = await this.getById(id);
    if (!existingTeacher) {
      throw new Error('Teacher not found');
    }

    let photoURL = existingTeacher.photoURL;
    if (data.photo) {
      const photoRef = ref(storage, `teachers/${existingTeacher.teacherId}/photo`);
      await uploadBytes(photoRef, data.photo);
      photoURL = await getDownloadURL(photoRef);
    }

    // Handle document uploads - keep existing documents and add new ones
    let documents = existingTeacher.documents || [];
    if (data.documents && data.documents.length > 0) {
      for (let i = 0; i < data.documents.length; i++) {
        const file = data.documents[i];
        const docId = `${existingTeacher.teacherId}_doc_${documents.length + i}_${Date.now()}`;
        const docRef = ref(storage, `teachers/${existingTeacher.teacherId}/documents/${docId}`);
        await uploadBytes(docRef, file);
        const docURL = await getDownloadURL(docRef);
        documents.push({
          id: docId,
          name: file.name,
          type: file.type,
          url: docURL,
          uploadedAt: new Date().toISOString()
        });
      }
    }

    const updatedData = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      aadharNumber: data.aadharNumber,
      subject: data.subject,
      department: data.department || '',
      qualification: data.qualification,
      dateOfJoining: data.dateOfJoining,
      salary: data.salary,
      photoURL,
      documents,
      bankDetails: data.bankDetails || existingTeacher.bankDetails || null,
      updatedAt: new Date().toISOString()
    };

    await updateDoc(doc(db, 'teachers', id), updatedData);
    return { ...existingTeacher, ...updatedData };
  },

  async delete(id: string): Promise<void> {
    const teacher = await this.getById(id);
    if (teacher?.photoURL) {
      const photoRef = ref(storage, `teachers/${teacher.teacherId}/photo`);
      await deleteObject(photoRef);
    }
    await deleteDoc(doc(db, 'teachers', id));
  }
};

// Fee Management API
export const feesApi = {
  async createFeeStructure(data: Omit<FeeStructure, 'id' | 'createdAt'>): Promise<FeeStructure> {
    const feeStructureData = {
      ...data,
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'feeStructures'), feeStructureData);
    return { id: docRef.id, ...feeStructureData };
  },

  async getFeeStructures(): Promise<FeeStructure[]> {
    const querySnapshot = await getDocs(
      query(collection(db, 'feeStructures'), orderBy('createdAt', 'desc'))
    );
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FeeStructure));
  },

  async getAllStudentFees(): Promise<StudentFee[]> {
    return schoolDataApi.studentFees.getAll();
  },

  async processPayment(data: FeePaymentFormData, createdBy: string): Promise<Payment> {
    return schoolDataApi.studentFees.processPayment(data, createdBy);
  }
};

// Salary Management API
export const salaryApi = {
  async processSalaryPayment(
    teacherId: string,
    amount: number,
    paymentMode: string,
    transactionId: string | undefined,
    createdBy: string
  ): Promise<SalaryPayment> {
    const now = new Date();
    const salaryData: Omit<SalaryPayment, 'id'> = {
      teacherId,
      month: now.getMonth() + 1,
      year: now.getFullYear(),
      amount,
      paymentDate: now.toISOString(),
      paymentMode: paymentMode as 'Cash' | 'Bank Transfer' | 'Cheque',
      transactionId,
      createdBy,
      createdAt: now.toISOString()
    };

    const docRef = await addDoc(collection(db, 'salaryPayments'), salaryData);
    return { id: docRef.id, ...salaryData };
  },

  async getTeacherSalaryHistory(teacherId: string): Promise<SalaryPayment[]> {
    const q = query(
      collection(db, 'salaryPayments'),
      where('teacherId', '==', teacherId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SalaryPayment));
  }
};

// Notices API
export const noticesApi = {
  async create(data: NoticeFormData, createdBy: string): Promise<Notice> {
    const noticeId = `NOT${Date.now()}`;
    let attachmentURL = '';

    if (data.attachment) {
      const attachmentRef = ref(storage, `notices/${noticeId}/attachment`);
      await uploadBytes(attachmentRef, data.attachment);
      attachmentURL = await getDownloadURL(attachmentRef);
    }

    const noticeData = {
      noticeId,
      title: data.title,
      description: data.description,
      date: data.date,
      priority: data.priority,
      attachmentURL,
      published: data.published,
      createdAt: new Date().toISOString(),
      createdBy,
      updatedAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'notices'), noticeData);
    return { id: docRef.id, ...noticeData };
  },

  async getAll(): Promise<Notice[]> {
    return schoolDataApi.notices.getAll();
  },

  async getById(id: string): Promise<Notice | null> {
    return schoolDataApi.notices.getById(id);
  },

  async update(id: string, data: Partial<Notice>): Promise<void> {
    return schoolDataApi.notices.update(id, data);
  },

  async delete(id: string): Promise<void> {
    return schoolDataApi.notices.delete(id);
  },

  async togglePublish(id: string, published: boolean): Promise<void> {
    return schoolDataApi.notices.togglePublish(id, published);
  }
};

// Fee Structure API (alias for compatibility with admission form)
export const feeStructureApi = {
  async getByClass(className: string, academicYear?: string): Promise<FeeStructure[]> {
    return schoolDataApi.feeStructures.getByClass(className, academicYear);
  },

  async getFeeStructures(): Promise<FeeStructure[]> {
    return schoolDataApi.feeStructures.getAll();
  },

  async getAll(): Promise<FeeStructure[]> {
    return schoolDataApi.feeStructures.getAll();
  },

  async create(data: FeeStructureFormData): Promise<FeeStructure> {
    return schoolDataApi.feeStructures.create(data);
  },

  async update(id: string, data: FeeStructureFormData): Promise<void> {
    return schoolDataApi.feeStructures.update(id, data);
  },

  async delete(id: string): Promise<void> {
    return schoolDataApi.feeStructures.delete(id);
  },

  async getById(id: string): Promise<FeeStructure | null> {
    return schoolDataApi.feeStructures.getById(id);
  }
};

// Dashboard API
export const dashboardApi = {
  async getStats(): Promise<DashboardStats> {
    const [
      studentsSnapshot,
      teachersSnapshot,
      pendingAdmissionsSnapshot,
      studentFeesSnapshot,
      noticesSnapshot
    ] = await Promise.all([
      getDocs(collection(db, 'students')),
      getDocs(collection(db, 'teachers')),
      getDocs(query(collection(db, 'students'), where('admissionStatus', '==', 'Pending'))),
      getDocs(collection(db, 'studentFees')),
      getDocs(query(collection(db, 'notices'), where('published', '==', true)))
    ]);

    const studentFees = studentFeesSnapshot.docs.map(doc => doc.data() as StudentFee);
    const totalFeeCollection = studentFees.reduce((sum, fee) => sum + fee.paidAmount, 0);
    const pendingFees = studentFees.reduce((sum, fee) => sum + fee.pendingAmount, 0);

    // Calculate monthly fee collection (current month)
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const monthlyFeeCollection = studentFees.reduce((sum, fee) => {
      const monthlyPayments = fee.payments.filter(payment => {
        const paymentDate = new Date(payment.date);
        return paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear;
      });
      return sum + monthlyPayments.reduce((monthSum, payment) => monthSum + payment.amount, 0);
    }, 0);

    return {
      totalStudents: studentsSnapshot.size,
      totalTeachers: teachersSnapshot.size,
      pendingAdmissions: pendingAdmissionsSnapshot.size,
      totalFeeCollection,
      monthlyFeeCollection,
      pendingFees,
      activeNotices: noticesSnapshot.size
    };
  },

  async getMonthlyFeeData(): Promise<{ month: string; amount: number }[]> {
    const studentFeesSnapshot = await getDocs(collection(db, 'studentFees'));
    const studentFees = studentFeesSnapshot.docs.map(doc => doc.data() as StudentFee);

    // Get last 12 months
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        year: date.getFullYear(),
        monthIndex: date.getMonth()
      });
    }

    return months.map(({ month, year, monthIndex }) => {
      const monthlyAmount = studentFees.reduce((sum, fee) => {
        const monthlyPayments = fee.payments.filter(payment => {
          const paymentDate = new Date(payment.date);
          return paymentDate.getMonth() === monthIndex && paymentDate.getFullYear() === year;
        });
        return sum + monthlyPayments.reduce((monthSum, payment) => monthSum + payment.amount, 0);
      }, 0);

      return { month, amount: monthlyAmount };
    });
  }
};

// Settings API - Classes
export const classesApi = {
  async create(data: ClassFormData): Promise<SchoolClass> {
    return schoolDataApi.classes.create(data);
  },

  async getAll(): Promise<SchoolClass[]> {
    return schoolDataApi.classes.getAll();
  },

  async getById(id: string): Promise<SchoolClass | null> {
    return schoolDataApi.classes.getById(id);
  },

  async update(id: string, data: ClassFormData): Promise<void> {
    return schoolDataApi.classes.update(id, data);
  },

  async delete(id: string): Promise<void> {
    return schoolDataApi.classes.delete(id);
  }
};

// Settings API - Subjects
export const subjectsApi = {
  async create(data: SubjectFormData): Promise<Subject> {
    return schoolDataApi.subjects.create(data);
  },

  async getAll(): Promise<Subject[]> {
    return schoolDataApi.subjects.getAll();
  },

  async getById(id: string): Promise<Subject | null> {
    return schoolDataApi.subjects.getById(id);
  },

  async update(id: string, data: SubjectFormData): Promise<void> {
    return schoolDataApi.subjects.update(id, data);
  },

  async delete(id: string): Promise<void> {
    return schoolDataApi.subjects.delete(id);
  }
};

// Settings API - Departments
export const departmentsApi = {
  async create(data: DepartmentFormData): Promise<Department> {
    return schoolDataApi.departments.create(data);
  },

  async getAll(): Promise<Department[]> {
    return schoolDataApi.departments.getAll();
  },

  async getById(id: string): Promise<Department | null> {
    return schoolDataApi.departments.getById(id);
  },

  async update(id: string, data: DepartmentFormData): Promise<void> {
    return schoolDataApi.departments.update(id, data);
  },

  async delete(id: string): Promise<void> {
    return schoolDataApi.departments.delete(id);
  }
};

// School Registration API
export const schoolRegistrationApi = {
  async register(formData: SchoolRegistrationFormData): Promise<string> {
    // Validate password confirmation
    if (formData.adminPassword !== formData.confirmPassword) {
      throw new Error('Passwords do not match');
    }

    if (formData.adminPassword.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }

    const registrationId = `SCH${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

    let schoolLogoURL = '';
    let userCredential;

    try {
      // Create the Firebase Auth user first — Storage rules require an
      // authenticated request, and uploading the logo before sign-in was
      // running the upload with no (or the wrong) auth context.
      userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.adminEmail,
        formData.adminPassword
      );

      // Upload school logo if provided. This is cosmetic/optional, so a
      // Storage failure (e.g. security rules rejecting the upload) shouldn't
      // block the rest of registration — the school can add a logo later.
      if (formData.schoolLogo) {
        try {
          const logoRef = ref(storage, `school-logos/${registrationId}`);
          const snapshot = await uploadBytes(logoRef, formData.schoolLogo);
          schoolLogoURL = await getDownloadURL(snapshot.ref);
        } catch (logoError) {
          console.error('Error uploading school logo, continuing registration without it:', logoError);
        }
      }

      const registrationData: any = {
        registrationId,
        schoolName: formData.schoolName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        adminEmail: formData.adminEmail,
        adminMobile: formData.adminMobile,
        principalName: formData.principalName,
        schoolType: formData.schoolType as SchoolRegistration['schoolType'],
        establishedYear: formData.establishedYear,
        affiliation: formData.affiliation as SchoolRegistration['affiliation'],
        // Schools are approved automatically on registration — no manual review step.
        status: 'Approved',
        approvedBy: 'system-auto-approval',
        approvedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Only add optional fields if they have values
      if (formData.principalEmail && formData.principalEmail.trim()) {
        registrationData.principalEmail = formData.principalEmail;
      }

      if (formData.principalMobile && formData.principalMobile.trim()) {
        registrationData.principalMobile = formData.principalMobile;
      }

      if (formData.totalStudents && formData.totalStudents.trim()) {
        registrationData.totalStudents = formData.totalStudents;
      }

      if (formData.website && formData.website.trim()) {
        registrationData.website = formData.website;
      }

      if (schoolLogoURL) {
        registrationData.schoolLogoURL = schoolLogoURL;
      }

      // Create school registration document
      const schoolDocRef = await addDoc(collection(db, 'schoolRegistrations'), registrationData);

      // Create admin user document
      const adminUserData: Omit<AdminUser, 'id'> = {
        uid: userCredential.user.uid,
        email: formData.adminEmail,
        schoolId: schoolDocRef.id,
        schoolName: formData.schoolName,
        role: 'admin',
        isActive: true, // Schools are approved automatically on registration
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'adminUsers'), adminUserData);

      // Create default school data
      await this.createDefaultSchoolData(schoolDocRef.id);

      return schoolDocRef.id;

    } catch (error: any) {
      // If there's an error after creating the user, we should clean up
      if (userCredential) {
        try {
          await userCredential.user.delete();
        } catch (cleanupError) {
          console.error('Error cleaning up user after failed registration:', cleanupError);
        }
      }

      // If there's an error after uploading logo, we should clean up
      if (schoolLogoURL) {
        try {
          const logoRef = ref(storage, `school-logos/${registrationId}`);
          await deleteObject(logoRef);
        } catch (cleanupError) {
          console.error('Error cleaning up logo after failed registration:', cleanupError);
        }
      }

      throw error;
    }
  },

  async getAll(): Promise<SchoolRegistration[]> {
    const querySnapshot = await getDocs(
      query(collection(db, 'schoolRegistrations'), orderBy('createdAt', 'desc'))
    );
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SchoolRegistration));
  },

  async getById(id: string): Promise<SchoolRegistration | null> {
    const docSnap = await getDoc(doc(db, 'schoolRegistrations', id));
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as SchoolRegistration : null;
  },

  async updateStatus(id: string, status: SchoolRegistration['status'], rejectionReason?: string): Promise<void> {
    const updateData: any = {
      status,
      updatedAt: new Date().toISOString()
    };

    if (status === 'Approved') {
      updateData.approvedBy = 'admin'; // You can pass the actual admin ID
      updateData.approvedAt = new Date().toISOString();

      // Activate the admin user when school is approved
      await this.activateAdminUser(id);
    } else if (status === 'Rejected' && rejectionReason) {
      updateData.rejectionReason = rejectionReason;
    }

    await updateDoc(doc(db, 'schoolRegistrations', id), updateData);
  },

  async activateAdminUser(schoolId: string): Promise<void> {
    try {
      // Find the admin user associated with this school
      const adminUsersSnapshot = await getDocs(
        query(collection(db, 'adminUsers'), where('schoolId', '==', schoolId))
      );

      if (!adminUsersSnapshot.empty) {
        const adminUserDoc = adminUsersSnapshot.docs[0];
        await updateDoc(adminUserDoc.ref, {
          isActive: true,
          updatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error activating admin user:', error);
    }
  },

  async delete(id: string): Promise<void> {
    // Get the registration data to delete the logo from storage
    const registration = await this.getById(id);

    if (registration?.schoolLogoURL) {
      try {
        const logoRef = ref(storage, `school-logos/${registration.registrationId}`);
        await deleteObject(logoRef);
      } catch (error) {
        console.warn('Error deleting school logo:', error);
      }
    }

    await deleteDoc(doc(db, 'schoolRegistrations', id));
  },

  async getPendingCount(): Promise<number> {
    const querySnapshot = await getDocs(
      query(collection(db, 'schoolRegistrations'), where('status', '==', 'Pending'))
    );
    return querySnapshot.size;
  },

  async createDefaultSchoolData(schoolId: string): Promise<void> {
    try {
      console.log('Creating default school data for school:', schoolId);

      // Default classes from Nursery to Class 12
      const defaultClasses = [
        { name: 'Nursery', sections: ['A', 'B'] },
        { name: 'LKG', sections: ['A', 'B'] },
        { name: 'UKG', sections: ['A', 'B'] },
        { name: 'Class 1', sections: ['A', 'B', 'C'] },
        { name: 'Class 2', sections: ['A', 'B', 'C'] },
        { name: 'Class 3', sections: ['A', 'B', 'C'] },
        { name: 'Class 4', sections: ['A', 'B', 'C'] },
        { name: 'Class 5', sections: ['A', 'B', 'C'] },
        { name: 'Class 6', sections: ['A', 'B', 'C'] },
        { name: 'Class 7', sections: ['A', 'B', 'C'] },
        { name: 'Class 8', sections: ['A', 'B', 'C'] },
        { name: 'Class 9', sections: ['A', 'B', 'C'] },
        { name: 'Class 10', sections: ['A', 'B', 'C'] },
        { name: 'Class 11', sections: ['A', 'B'] },
        { name: 'Class 12', sections: ['A', 'B'] }
      ];

      // Create classes
      for (const classData of defaultClasses) {
        const classDoc = {
          name: classData.name,
          sections: classData.sections,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        await addDoc(collection(db, 'schools', schoolId, 'classes'), classDoc);
      }

      // Default subjects for different levels
      const defaultSubjects = [
        // Pre-primary (Nursery, LKG, UKG)
        { name: 'English', code: 'ENG', levels: ['Nursery', 'LKG', 'UKG'] },
        { name: 'Mathematics', code: 'MATH', levels: ['Nursery', 'LKG', 'UKG'] },
        { name: 'Environmental Studies', code: 'EVS', levels: ['Nursery', 'LKG', 'UKG'] },
        { name: 'Drawing & Colouring', code: 'DRAW', levels: ['Nursery', 'LKG', 'UKG'] },
        { name: 'Physical Education', code: 'PE', levels: ['Nursery', 'LKG', 'UKG'] },

        // Primary (Class 1 to Class 5)
        { name: 'English', code: 'ENG', levels: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'] },
        { name: 'Hindi', code: 'HIN', levels: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'] },
        { name: 'Mathematics', code: 'MATH', levels: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'] },
        { name: 'Environmental Studies', code: 'EVS', levels: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'] },
        { name: 'Computer Science', code: 'CS', levels: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'] },
        { name: 'Art & Craft', code: 'ART', levels: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'] },
        { name: 'Physical Education', code: 'PE', levels: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'] },

        // Middle School (Class 6 to Class 8)
        { name: 'English', code: 'ENG', levels: ['Class 6', 'Class 7', 'Class 8'] },
        { name: 'Hindi', code: 'HIN', levels: ['Class 6', 'Class 7', 'Class 8'] },
        { name: 'Mathematics', code: 'MATH', levels: ['Class 6', 'Class 7', 'Class 8'] },
        { name: 'Science', code: 'SCI', levels: ['Class 6', 'Class 7', 'Class 8'] },
        { name: 'Social Studies', code: 'SST', levels: ['Class 6', 'Class 7', 'Class 8'] },
        { name: 'Computer Science', code: 'CS', levels: ['Class 6', 'Class 7', 'Class 8'] },
        { name: 'Physical Education', code: 'PE', levels: ['Class 6', 'Class 7', 'Class 8'] },
        { name: 'Art Education', code: 'ART', levels: ['Class 6', 'Class 7', 'Class 8'] },

        // High School (Class 9 to Class 10)
        { name: 'English', code: 'ENG', levels: ['Class 9', 'Class 10'] },
        { name: 'Hindi', code: 'HIN', levels: ['Class 9', 'Class 10'] },
        { name: 'Mathematics', code: 'MATH', levels: ['Class 9', 'Class 10'] },
        { name: 'Science', code: 'SCI', levels: ['Class 9', 'Class 10'] },
        { name: 'Social Science', code: 'SST', levels: ['Class 9', 'Class 10'] },
        { name: 'Computer Applications', code: 'CA', levels: ['Class 9', 'Class 10'] },
        { name: 'Physical Education', code: 'PE', levels: ['Class 9', 'Class 10'] },

        // Senior Secondary (Class 11 to Class 12) - Core subjects
        { name: 'English Core', code: 'ENG_CORE', levels: ['Class 11', 'Class 12'] },
        { name: 'Physics', code: 'PHY', levels: ['Class 11', 'Class 12'] },
        { name: 'Chemistry', code: 'CHEM', levels: ['Class 11', 'Class 12'] },
        { name: 'Mathematics', code: 'MATH', levels: ['Class 11', 'Class 12'] },
        { name: 'Biology', code: 'BIO', levels: ['Class 11', 'Class 12'] },
        { name: 'Computer Science', code: 'CS', levels: ['Class 11', 'Class 12'] },
        { name: 'Accountancy', code: 'ACC', levels: ['Class 11', 'Class 12'] },
        { name: 'Business Studies', code: 'BS', levels: ['Class 11', 'Class 12'] },
        { name: 'Economics', code: 'ECO', levels: ['Class 11', 'Class 12'] },
        { name: 'Political Science', code: 'POL', levels: ['Class 11', 'Class 12'] },
        { name: 'History', code: 'HIST', levels: ['Class 11', 'Class 12'] },
        { name: 'Geography', code: 'GEO', levels: ['Class 11', 'Class 12'] },
        { name: 'Psychology', code: 'PSY', levels: ['Class 11', 'Class 12'] },
        { name: 'Physical Education', code: 'PE', levels: ['Class 11', 'Class 12'] }
      ];

      // Merge subjects that repeat across level brackets (e.g. Mathematics spans
      // every bracket) into a single subject document per unique name+code.
      const mergedSubjects = new Map<string, { name: string; code: string; levels: string[] }>();
      for (const subject of defaultSubjects) {
        const key = `${subject.name.toLowerCase()}|${subject.code.toLowerCase()}`;
        const existing = mergedSubjects.get(key);
        if (existing) {
          existing.levels.push(...subject.levels);
        } else {
          mergedSubjects.set(key, { name: subject.name, code: subject.code, levels: [...subject.levels] });
        }
      }

      // Create subjects
      for (const subject of mergedSubjects.values()) {
        const subjectDoc = {
          name: subject.name,
          code: subject.code,
          description: `${subject.name} for classes: ${subject.levels.join(', ')}`,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        await addDoc(collection(db, 'schools', schoolId, 'subjects'), subjectDoc);
      }

      // Default departments
      const defaultDepartments = [
        { name: 'Pre-Primary', description: 'Nursery, LKG, UKG classes' },
        { name: 'Primary', description: 'Class 1 to Class 5' },
        { name: 'Middle School', description: 'Class 6 to Class 8' },
        { name: 'High School', description: 'Class 9 to Class 10' },
        { name: 'Senior Secondary', description: 'Class 11 to Class 12' },
        { name: 'Science', description: 'Physics, Chemistry, Biology, Mathematics' },
        { name: 'Commerce', description: 'Accountancy, Business Studies, Economics' },
        { name: 'Humanities', description: 'History, Geography, Political Science, Psychology' },
        { name: 'Computer Science', description: 'Computer Applications and Programming' },
        { name: 'Physical Education', description: 'Sports and Physical Training' },
        { name: 'Arts', description: 'Drawing, Painting, Art & Craft' },
        { name: 'Administration', description: 'Principal, Vice Principal, Admin Staff' }
      ];

      // Create departments
      for (const department of defaultDepartments) {
        const departmentDoc = {
          name: department.name,
          description: department.description,
          headOfDepartment: '',
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        await addDoc(collection(db, 'schools', schoolId, 'departments'), departmentDoc);
      }

      // Initialize the school-wide academic year so fee structures/bills/results
      // have a sensible session to attach to from day one, instead of relying
      // on whatever happens to call getAcademicYear() first.
      await schoolDataApi.settings.setAcademicYear(getCurrentAcademicYear(), schoolId);

      console.log('Default school data created successfully for school:', schoolId);
    } catch (error) {
      console.error('Error creating default school data:', error);
      // Don't throw the error to avoid breaking the registration process
    }
  }
};

// User and School Context API
export const userApi = {
  async getCurrentUserSchool(): Promise<{ adminUser: AdminUser | null; school: SchoolRegistration | null }> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user found');
      return { adminUser: null, school: null };
    }

    try {
      console.log('Getting admin user for UID:', currentUser.uid);

      // Get admin user document
      const adminUsersSnapshot = await getDocs(
        query(collection(db, 'adminUsers'), where('uid', '==', currentUser.uid))
      );

      if (adminUsersSnapshot.empty) {
        console.error('No admin user document found for UID:', currentUser.uid);
        console.log('User email:', currentUser.email);
        return { adminUser: null, school: null };
      }

      const adminUserDoc = adminUsersSnapshot.docs[0];
      const adminUser = { id: adminUserDoc.id, ...adminUserDoc.data() } as AdminUser;
      console.log('Found admin user:', adminUser);

      // Get school details
      const schoolDoc = await getDoc(doc(db, 'schoolRegistrations', adminUser.schoolId));

      if (!schoolDoc.exists()) {
        console.error('School document not found for ID:', adminUser.schoolId);
        return { adminUser, school: null };
      }

      const school = { id: schoolDoc.id, ...schoolDoc.data() } as SchoolRegistration;
      console.log('Found school:', school.schoolName);

      return { adminUser, school };
    } catch (error) {
      console.error('Error getting current user school:', error);
      return { adminUser: null, school: null };
    }
  }
};

// School-specific data API with nested collections
export const schoolDataApi = {
  // Helper to get current school ID
  async getCurrentSchoolId(): Promise<string | null> {
    const { adminUser } = await userApi.getCurrentUserSchool();
    return adminUser?.schoolId || null;
  },

  // School-wide settings (single documents, not collections of records)
  settings: {
    /** The one current academic year (e.g. "2026-2027") every fee structure, bill and result should use. */
    async getAcademicYear(schoolId?: string): Promise<string> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const ref = doc(db, 'schools', currentSchoolId, 'settings', 'academicYear');
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) return docSnap.data().year;

      const defaultYear = getCurrentAcademicYear();
      await setDoc(ref, { year: defaultYear, updatedAt: new Date().toISOString() });
      return defaultYear;
    },

    async setAcademicYear(year: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await setDoc(doc(db, 'schools', currentSchoolId, 'settings', 'academicYear'), {
        year,
        updatedAt: new Date().toISOString()
      });
    }
  },

  // Student Registrations API for specific school
  registrations: {
    async create(data: StudentRegistrationFormData, schoolId?: string): Promise<StudentRegistration> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const registrationId = `REG${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

      const registrationData = {
        registrationId,
        name: data.name,
        dateOfBirth: data.dateOfBirth,
        gender: data.gender,
        parentName: data.parentName,
        parentContact: data.parentContact,
        parentEmail: data.parentEmail,
        preferredClass: data.preferredClass,
        registrationStatus: 'Pending' as const,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'registrations'), registrationData);
      return { id: docRef.id, ...registrationData };
    },

    async getAll(schoolId?: string): Promise<StudentRegistration[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'registrations'), orderBy('createdAt', 'desc'))
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StudentRegistration));
    },

    async getById(id: string, schoolId?: string): Promise<StudentRegistration | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'registrations', id));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as StudentRegistration : null;
    },

    async approve(id: string, approvedBy: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'registrations', id), {
        registrationStatus: 'Approved',
        approvedBy,
        approvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    },

    async reject(id: string, rejectionReason: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'registrations', id), {
        registrationStatus: 'Rejected',
        rejectionReason,
        updatedAt: new Date().toISOString()
      });
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'registrations', id));
    }
  },

  // Students API for specific school
  students: {
    async getAll(schoolId?: string): Promise<Student[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'students'), orderBy('createdAt', 'desc'))
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Student));
    },

    async create(studentData: StudentFormData, schoolId?: string): Promise<string> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const studentId = `STU${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

      const newStudent: Omit<Student, 'id'> = {
        studentId,
        registrationId: '', // Will be set if coming from registration
        name: studentData.name,
        dateOfBirth: studentData.dateOfBirth,
        gender: studentData.gender,
        class: studentData.class,
        parentName: studentData.parentName,
        parentContact: studentData.parentContact,
        parentEmail: studentData.parentEmail,
        permanentAddress: studentData.permanentAddress,
        currentAddress: studentData.currentAddress,
        admissionStatus: 'Approved',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'students'), newStudent);
      return docRef.id;
    },

    async getById(id: string, schoolId?: string): Promise<Student | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'students', id));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Student : null;
    },

    async update(id: string, data: Partial<Student>, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'students', id), {
        ...data,
        updatedAt: new Date().toISOString()
      });
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'students', id));
    },

    // Draft admission functionality
    async saveDraftAdmission(registrationId: string, draftData: Partial<StudentAdmissionFormData>, currentStep: number, schoolId?: string, status: string = 'draft'): Promise<string> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      // Save directly to admissions collection with specified status
      const admissionId = `ADM_${registrationId}`;
      const admissionData = {
        registrationId,
        formData: draftData,
        currentStep,
        status, // Use provided status or default to 'draft'
        lastSaved: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'schools', currentSchoolId, 'admissions', admissionId), admissionData, { merge: true });
      console.log('Saved admission to admissions collection with status:', status, 'ID:', admissionId);
      return admissionId;
    },

    async getDraftAdmission(registrationId: string, schoolId?: string): Promise<{ formData: Partial<StudentAdmissionFormData>; currentStep: number } | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      // Load from admissions collection instead of admissionDrafts
      const admissionId = `ADM_${registrationId}`;
      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'admissions', admissionId));

      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('Loaded admission draft from admissions collection:', admissionId);
        return {
          formData: data.formData || {},
          currentStep: data.currentStep || 1
        };
      }
      return null;
    },

    async deleteDraftAdmission(registrationId: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      // Instead of deleting, we'll update the status to 'submitted' when form is completed
      const admissionId = `ADM_${registrationId}`;
      await updateDoc(doc(db, 'schools', currentSchoolId, 'admissions', admissionId), {
        status: 'submitted',
        submittedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('Marked admission as submitted:', admissionId);
    }
  },

  // Teachers API for specific school
  teachers: {
    async getAll(schoolId?: string): Promise<Teacher[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'teachers'), orderBy('createdAt', 'desc'))
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Teacher));
    },

    async create(teacherData: TeacherFormData, schoolId?: string): Promise<string> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const teacherId = `TCH${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

      const newTeacher: Omit<Teacher, 'id'> = {
        teacherId,
        name: teacherData.name,
        email: teacherData.email,
        phone: teacherData.phone,
        aadharNumber: teacherData.aadharNumber,
        subject: teacherData.subject,
        department: teacherData.department || '',
        qualification: teacherData.qualification,
        dateOfJoining: teacherData.dateOfJoining,
        salary: teacherData.salary,
        documents: [],
        bankDetails: teacherData.bankDetails,
        status: 'Active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'teachers'), newTeacher);
      return docRef.id;
    },

    async getById(id: string, schoolId?: string): Promise<Teacher | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'teachers', id));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Teacher : null;
    },

    async update(id: string, data: Partial<Teacher>, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'teachers', id), {
        ...data,
        updatedAt: new Date().toISOString()
      });
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'teachers', id));
    }
  },

  // Notices API for specific school
  notices: {
    async create(data: NoticeFormData, createdBy: string, schoolId?: string): Promise<Notice> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const noticeId = `NOT${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
      let attachmentURL = '';

      if (data.attachment) {
        const attachmentRef = ref(storage, `schools/${currentSchoolId}/notices/${noticeId}/attachment`);
        await uploadBytes(attachmentRef, data.attachment);
        attachmentURL = await getDownloadURL(attachmentRef);
      }

      const noticeData = {
        noticeId,
        title: data.title,
        description: data.description,
        date: data.date,
        priority: data.priority,
        attachmentURL,
        published: data.published,
        createdAt: new Date().toISOString(),
        createdBy,
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'notices'), noticeData);
      return { id: docRef.id, ...noticeData };
    },

    async getAll(schoolId?: string): Promise<Notice[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'notices'), orderBy('createdAt', 'desc'))
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Notice));
    },

    async getById(id: string, schoolId?: string): Promise<Notice | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'notices', id));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Notice : null;
    },

    async update(id: string, data: Partial<Notice>, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'notices', id), {
        ...data,
        updatedAt: new Date().toISOString()
      });
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const notice = await this.getById(id, currentSchoolId);
      if (notice?.attachmentURL) {
        const attachmentRef = ref(storage, `schools/${currentSchoolId}/notices/${notice.noticeId}/attachment`);
        await deleteObject(attachmentRef);
      }
      await deleteDoc(doc(db, 'schools', currentSchoolId, 'notices', id));
    },

    async togglePublish(id: string, published: boolean, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'notices', id), {
        published,
        updatedAt: new Date().toISOString()
      });
    }
  },

  // Fee Structures API for specific school
  feeStructures: {
    // Uses the human-readable structureId as the actual Firestore document ID,
    // so a duplicate structureId is structurally impossible rather than just
    // discouraged by a client-side check.
    async create(data: FeeStructureFormData, schoolId?: string): Promise<FeeStructure> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docRef = doc(db, 'schools', currentSchoolId, 'feeStructures', data.structureId);
      const existing = await getDoc(docRef);
      if (existing.exists()) {
        throw new Error(`A fee structure with ID "${data.structureId}" already exists`);
      }

      const totalAmount = data.feeCategories.reduce((sum, category) => sum + category.amount, 0);

      const feeStructureData = {
        structureId: data.structureId,
        className: data.className,
        academicYear: data.academicYear,
        type: data.type,
        feeCategories: data.feeCategories,
        totalAmount,
        isActive: data.isActive,
        createdAt: new Date().toISOString()
      };

      await setDoc(docRef, feeStructureData);
      return { id: docRef.id, ...feeStructureData };
    },

    async getAll(schoolId?: string): Promise<FeeStructure[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'feeStructures'), orderBy('createdAt', 'desc'))
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FeeStructure));
    },

    async getByClass(className: string, academicYear?: string, schoolId?: string): Promise<FeeStructure[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      let q = query(
        collection(db, 'schools', currentSchoolId, 'feeStructures'),
        where('className', '==', className)
      );

      if (academicYear) {
        q = query(q, where('academicYear', '==', academicYear));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FeeStructure));
    },

    async getById(id: string, schoolId?: string): Promise<FeeStructure | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'feeStructures', id));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as FeeStructure : null;
    },

    async update(id: string, data: FeeStructureFormData, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const totalAmount = data.feeCategories.reduce((sum, category) => sum + category.amount, 0);

      await updateDoc(doc(db, 'schools', currentSchoolId, 'feeStructures', id), {
        structureId: data.structureId,
        className: data.className,
        academicYear: data.academicYear,
        type: data.type,
        feeCategories: data.feeCategories,
        totalAmount,
        isActive: data.isActive,
        updatedAt: new Date().toISOString()
      });
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'feeStructures', id));
    }
  },

  // Student Fees API for specific school
  studentFees: {
    async getAll(schoolId?: string): Promise<StudentFee[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(collection(db, 'schools', currentSchoolId, 'studentFees'));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StudentFee));
    },

    async getByStudentId(studentId: string, schoolId?: string): Promise<StudentFee | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const q = query(collection(db, 'schools', currentSchoolId, 'studentFees'), where('studentId', '==', studentId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) return null;

      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() } as StudentFee;
    },

    /** All fee records (one per academic-year session) for a student — used for a full session-wise payment history. */
    async getAllByStudentId(studentId: string, schoolId?: string): Promise<StudentFee[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const q = query(collection(db, 'schools', currentSchoolId, 'studentFees'), where('studentId', '==', studentId));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as StudentFee))
        .sort((a, b) => b.academicYear.localeCompare(a.academicYear));
    },

    async createStudentFeeRecord(studentId: string, className: string, feeStructure: FeeStructure, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const studentFeeData: Omit<StudentFee, 'id'> = {
        studentId,
        feeStructureId: feeStructure.id,
        className,
        academicYear: feeStructure.academicYear,
        feeStructure: feeStructure.feeCategories,
        totalFees: feeStructure.totalAmount,
        paidAmount: 0,
        pendingAmount: feeStructure.totalAmount,
        payments: [],
        status: 'Pending'
      };

      await addDoc(collection(db, 'schools', currentSchoolId, 'studentFees'), studentFeeData);
    },

    /**
     * Ensures the student has a fee ledger ("bill") for the current session
     * matching the active monthly FeeStructure for their class — creating one
     * per academic year as sessions pass, or updating the current session's
     * record if the class (and therefore the applicable structure) changed
     * mid-year. Silently does nothing if no active monthly structure exists.
     */
    async syncMonthlyFeeForStudent(studentId: string, className: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const structures = await schoolDataApi.feeStructures.getByClass(className, undefined, currentSchoolId);
      const monthlyStructure = structures.find(fs => fs.type === 'monthly' && fs.isActive);
      if (!monthlyStructure) return;

      const existingRecords = await schoolDataApi.studentFees.getAllByStudentId(studentId, currentSchoolId);
      const existing = existingRecords.find(r => r.academicYear === monthlyStructure.academicYear);

      if (!existing) {
        await schoolDataApi.studentFees.createStudentFeeRecord(studentId, className, monthlyStructure, currentSchoolId);
        return;
      }

      if (existing.feeStructureId === monthlyStructure.id) return;

      const paidAmount = existing.paidAmount || 0;
      const pendingAmount = Math.max(monthlyStructure.totalAmount - paidAmount, 0);
      await updateDoc(doc(db, 'schools', currentSchoolId, 'studentFees', existing.id), {
        feeStructureId: monthlyStructure.id,
        className,
        academicYear: monthlyStructure.academicYear,
        feeStructure: monthlyStructure.feeCategories,
        totalFees: monthlyStructure.totalAmount,
        pendingAmount,
        status: pendingAmount <= 0 ? 'Paid' : paidAmount > 0 ? 'Partial' : 'Pending'
      });
    },

    /**
     * Generates a single bill for one student, for one billing period. Returns
     * 'skipped' instead of creating a duplicate if a bill already exists for
     * the same (studentId, billType, academicYear, month) combination.
     */
    async generateBill(
      params: {
        studentId: string;
        className: string;
        billType: 'monthly' | 'admission' | 'annual' | 'custom';
        academicYear: string;
        month?: string;
        feeCategories: FeeCategory[];
        description?: string;
        dueDate?: string;
        feeStructureId?: string;
      },
      schoolId?: string
    ): Promise<'created' | 'skipped'> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const existingRecords = await schoolDataApi.studentFees.getAllByStudentId(params.studentId, currentSchoolId);
      const duplicate = existingRecords.find(r =>
        (r.billType || 'monthly') === params.billType &&
        r.academicYear === params.academicYear &&
        (r.month || undefined) === (params.month || undefined)
      );
      if (duplicate) return 'skipped';

      const totalAmount = params.feeCategories.reduce((sum, c) => sum + c.amount, 0);
      const billData: Record<string, unknown> = {
        studentId: params.studentId,
        billType: params.billType,
        billNumber: `BILL${Date.now()}${Math.floor(Math.random() * 1000)}`,
        generatedAt: new Date().toISOString(),
        className: params.className,
        academicYear: params.academicYear,
        feeStructure: params.feeCategories,
        totalFees: totalAmount,
        paidAmount: 0,
        pendingAmount: totalAmount,
        payments: [],
        status: 'Pending'
      };
      // Firestore rejects `undefined` field values, so only include optional fields when provided.
      if (params.month !== undefined) billData.month = params.month;
      if (params.description !== undefined) billData.description = params.description;
      if (params.dueDate !== undefined) billData.dueDate = params.dueDate;
      if (params.feeStructureId !== undefined) billData.feeStructureId = params.feeStructureId;

      await addDoc(collection(db, 'schools', currentSchoolId, 'studentFees'), billData);
      return 'created';
    },

    /** Generates the same bill for every approved student in one class. */
    async generateBillsForClass(
      params: {
        className: string;
        billType: 'monthly' | 'admission' | 'annual' | 'custom';
        academicYear: string;
        month?: string;
        feeCategories?: FeeCategory[];
        description?: string;
        dueDate?: string;
      },
      schoolId?: string
    ): Promise<{ created: number; skipped: number; errors: number }> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const allStudents = await schoolDataApi.students.getAll(currentSchoolId);
      const classStudents = allStudents.filter(s => s.class === params.className && s.admissionStatus === 'Approved');

      let feeCategories = params.feeCategories;
      let feeStructureId: string | undefined;
      if (params.billType !== 'custom') {
        const structures = await schoolDataApi.feeStructures.getByClass(params.className, params.academicYear, currentSchoolId);
        const structure = structures.find(fs => fs.type === params.billType && fs.isActive);
        if (!structure) {
          throw new Error(`No active ${params.billType} fee structure found for ${params.className}`);
        }
        feeCategories = structure.feeCategories;
        feeStructureId = structure.id;
      }
      if (!feeCategories || feeCategories.length === 0) {
        throw new Error('No fee categories to bill');
      }

      let created = 0;
      let skipped = 0;
      let errors = 0;
      for (const student of classStudents) {
        try {
          const result = await schoolDataApi.studentFees.generateBill({
            studentId: student.id,
            className: student.class,
            billType: params.billType,
            academicYear: params.academicYear,
            month: params.month,
            feeCategories: feeCategories!,
            description: params.description,
            dueDate: params.dueDate,
            feeStructureId
          }, currentSchoolId);
          if (result === 'created') created++; else skipped++;
        } catch (error) {
          console.error(`Error generating bill for ${student.name}:`, error);
          errors++;
        }
      }
      return { created, skipped, errors };
    },

    /** Generates the same type of bill for every approved student in the school, using each student's own class's fee structure (skipped for custom bills you must scope to a class/student). */
    async generateBillsForSchool(
      params: {
        billType: 'monthly' | 'admission' | 'annual' | 'custom';
        academicYear: string;
        month?: string;
        feeCategories?: FeeCategory[];
        description?: string;
        dueDate?: string;
      },
      schoolId?: string
    ): Promise<{ created: number; skipped: number; errors: number }> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const [allStudents, allStructures] = await Promise.all([
        schoolDataApi.students.getAll(currentSchoolId),
        params.billType !== 'custom' ? schoolDataApi.feeStructures.getAll(currentSchoolId) : Promise.resolve([] as FeeStructure[])
      ]);

      const approvedStudents = allStudents.filter(s => s.admissionStatus === 'Approved');

      let created = 0;
      let skipped = 0;
      let errors = 0;
      for (const student of approvedStudents) {
        try {
          let feeCategories = params.feeCategories;
          let feeStructureId: string | undefined;
          if (params.billType !== 'custom') {
            const structure = allStructures.find(
              fs => fs.className === student.class && fs.type === params.billType && fs.academicYear === params.academicYear && fs.isActive
            );
            if (!structure) {
              skipped++;
              continue;
            }
            feeCategories = structure.feeCategories;
            feeStructureId = structure.id;
          }
          if (!feeCategories || feeCategories.length === 0) {
            skipped++;
            continue;
          }

          const result = await schoolDataApi.studentFees.generateBill({
            studentId: student.id,
            className: student.class,
            billType: params.billType,
            academicYear: params.academicYear,
            month: params.month,
            feeCategories,
            description: params.description,
            dueDate: params.dueDate,
            feeStructureId
          }, currentSchoolId);
          if (result === 'created') created++; else skipped++;
        } catch (error) {
          console.error(`Error generating bill for ${student.name}:`, error);
          errors++;
        }
      }
      return { created, skipped, errors };
    },

    /** Updates a bill's editable fields (due date, description, charges). Recomputes totals/status when charges change. */
    async update(
      id: string,
      data: { description?: string; dueDate?: string; month?: string; feeCategories?: FeeCategory[] },
      schoolId?: string
    ): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const billRef = doc(db, 'schools', currentSchoolId, 'studentFees', id);
      const updateData: Record<string, unknown> = {};
      if (data.description !== undefined) updateData.description = data.description;
      if (data.dueDate !== undefined) updateData.dueDate = data.dueDate;
      if (data.month !== undefined) updateData.month = data.month;

      if (data.feeCategories) {
        const billSnap = await getDoc(billRef);
        if (!billSnap.exists()) throw new Error('Bill not found');
        const existing = billSnap.data() as StudentFee;
        const totalFees = data.feeCategories.reduce((sum, c) => sum + c.amount, 0);
        const pendingAmount = Math.max(totalFees - existing.paidAmount, 0);
        updateData.feeStructure = data.feeCategories;
        updateData.totalFees = totalFees;
        updateData.pendingAmount = pendingAmount;
        updateData.status = pendingAmount <= 0 ? 'Paid' : existing.paidAmount > 0 ? 'Partial' : 'Pending';
      }

      await updateDoc(billRef, updateData);
    },

    /** Permanently removes a bill. Does not touch any payments already recorded against it. */
    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'studentFees', id));
    },

    /** Records a payment against one specific bill (data.studentFeeId) — a student can have many bills, so the target must be explicit rather than guessed. */
    async processPayment(data: FeePaymentFormData, createdBy: string, schoolId?: string): Promise<Payment> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const receiptNo = `RCP${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

      // Firestore rejects `undefined` field values, so only include transactionId when provided.
      const payment: Omit<Payment, 'id'> = {
        amount: data.amount,
        date: data.date,
        mode: data.paymentMode,
        ...(data.transactionId !== undefined ? { transactionId: data.transactionId } : {}),
        receiptNo,
        createdBy
      };

      // Add payment to payments collection
      const paymentDocRef = await addDoc(collection(db, 'schools', currentSchoolId, 'payments'), payment);

      const billRef = doc(db, 'schools', currentSchoolId, 'studentFees', data.studentFeeId);
      const billSnap = await getDoc(billRef);
      if (!billSnap.exists()) throw new Error('Bill not found');
      const currentData = billSnap.data() as StudentFee;

      const newPaidAmount = currentData.paidAmount + data.amount;
      const newPendingAmount = Math.max(currentData.totalFees - newPaidAmount, 0);

      await updateDoc(billRef, {
        paidAmount: newPaidAmount,
        pendingAmount: newPendingAmount,
        lastPaymentDate: data.date,
        status: newPendingAmount <= 0 ? 'Paid' : 'Partial',
        payments: [...currentData.payments, { id: paymentDocRef.id, ...payment }]
      });

      return { id: paymentDocRef.id, ...payment };
    }
  },

  // Classes API for specific school
  classes: {
    async create(data: ClassFormData, schoolId?: string): Promise<SchoolClass> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const classData = {
        name: data.name,
        sections: data.sections,
        isActive: data.isActive,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'classes'), classData);
      return { id: docRef.id, ...classData };
    },

    async getAll(schoolId?: string): Promise<SchoolClass[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'classes'), orderBy('name', 'asc'))
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SchoolClass));
    },

    async getById(id: string, schoolId?: string): Promise<SchoolClass | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'classes', id));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as SchoolClass : null;
    },

    async update(id: string, data: ClassFormData, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'classes', id), {
        ...data,
        updatedAt: new Date().toISOString()
      });
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'classes', id));
    }
  },

  // Attendance API for specific school
  attendance: {
    async getByClassDate(classId: string, section: string, date: string, schoolId?: string): Promise<AttendanceRecord | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'attendance', `${classId}_${section}_${date}`));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as AttendanceRecord : null;
    },

    async save(record: Omit<AttendanceRecord, 'id' | 'createdAt' | 'updatedAt'>, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docId = `${record.classId}_${record.section}_${record.date}`;
      const existing = await getDoc(doc(db, 'schools', currentSchoolId, 'attendance', docId));
      await setDoc(doc(db, 'schools', currentSchoolId, 'attendance', docId), {
        ...record,
        createdAt: existing.exists() ? existing.data().createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    },

    /**
     * A student's attendance history spans every class/section they've ever
     * been marked in, and Firestore can't query inside the `entries` array
     * for a specific studentId, so this scans all attendance docs for the
     * school and picks out this student's entry from each one.
     */
    async getForStudent(studentId: string, schoolId?: string): Promise<{ date: string; status: AttendanceRecord['entries'][number]['status']; className: string; section: string }[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(collection(db, 'schools', currentSchoolId, 'attendance'));
      const records: { date: string; status: AttendanceRecord['entries'][number]['status']; className: string; section: string }[] = [];

      querySnapshot.docs.forEach(docSnap => {
        const data = docSnap.data() as AttendanceRecord;
        const entry = data.entries?.find(e => e.studentId === studentId);
        if (entry) {
          records.push({ date: data.date, status: entry.status, className: data.className, section: data.section });
        }
      });

      return records.sort((a, b) => a.date.localeCompare(b.date));
    }
  },

  // Results API for specific school
  results: {
    async getByStudentId(studentId: string, schoolId?: string): Promise<ExamResult[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const q = query(collection(db, 'schools', currentSchoolId, 'results'), where('studentId', '==', studentId));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as ExamResult))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    },

    async create(data: Omit<ExamResult, 'id' | 'createdAt' | 'updatedAt'>, schoolId?: string): Promise<ExamResult> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const resultData: any = {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      // Firestore rejects `undefined` field values, so only include optional
      // fields (e.g. section, remarks) when they were actually provided.
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined) resultData[key] = value;
      });
      const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'results'), resultData);
      return { id: docRef.id, ...resultData };
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'results', id));
    }
  },

  // Subjects API for specific school
  subjects: {
    async create(data: SubjectFormData, schoolId?: string): Promise<Subject> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const subjectData = {
        name: data.name,
        code: data.code,
        description: data.description || '',
        isActive: data.isActive,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'subjects'), subjectData);
      return { id: docRef.id, ...subjectData };
    },

    async getAll(schoolId?: string): Promise<Subject[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'subjects'), orderBy('name', 'asc'))
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Subject));
    },

    async getById(id: string, schoolId?: string): Promise<Subject | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'subjects', id));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Subject : null;
    },

    async update(id: string, data: SubjectFormData, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'subjects', id), {
        ...data,
        updatedAt: new Date().toISOString()
      });
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'subjects', id));
    }
  },

  // Departments API for specific school
  departments: {
    async create(data: DepartmentFormData, schoolId?: string): Promise<Department> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const departmentData = {
        name: data.name,
        description: data.description || '',
        headOfDepartment: data.headOfDepartment || '',
        isActive: data.isActive,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const docRef = await addDoc(collection(db, 'schools', currentSchoolId, 'departments'), departmentData);
      return { id: docRef.id, ...departmentData };
    },

    async getAll(schoolId?: string): Promise<Department[]> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const querySnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'departments'), orderBy('name', 'asc'))
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Department));
    },

    async getById(id: string, schoolId?: string): Promise<Department | null> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      const docSnap = await getDoc(doc(db, 'schools', currentSchoolId, 'departments', id));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Department : null;
    },

    async update(id: string, data: DepartmentFormData, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await updateDoc(doc(db, 'schools', currentSchoolId, 'departments', id), {
        ...data,
        updatedAt: new Date().toISOString()
      });
    },

    async delete(id: string, schoolId?: string): Promise<void> {
      const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
      if (!currentSchoolId) throw new Error('No school context found');

      await deleteDoc(doc(db, 'schools', currentSchoolId, 'departments', id));
    }
  },

  // Dashboard stats for specific school
  async getDashboardStats(schoolId?: string): Promise<DashboardStats> {
    const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
    if (!currentSchoolId) throw new Error('No school context found');

    try {
      // Get students count
      const studentsSnapshot = await getDocs(collection(db, 'schools', currentSchoolId, 'students'));
      const totalStudents = studentsSnapshot.size;

      // Get teachers count
      const teachersSnapshot = await getDocs(collection(db, 'schools', currentSchoolId, 'teachers'));
      const totalTeachers = teachersSnapshot.size;

      // Get pending admissions count
      const pendingAdmissionsSnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'students'), where('admissionStatus', '==', 'Form_Pending'))
      );
      const pendingAdmissions = pendingAdmissionsSnapshot.size;

      // Get fees data
      const feesSnapshot = await getDocs(collection(db, 'schools', currentSchoolId, 'studentFees'));
      const feesData = feesSnapshot.docs.map(doc => doc.data() as StudentFee);

      const totalFeeCollection = feesData.reduce((sum, fee) => sum + fee.paidAmount, 0);
      const pendingFees = feesData.reduce((sum, fee) => sum + fee.pendingAmount, 0);

      // Calculate monthly fee collection (current month)
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      const monthlyFeeCollection = feesData.reduce((sum, fee) => {
        const monthlyPayments = fee.payments.filter(payment => {
          const paymentDate = new Date(payment.date);
          return paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear;
        });
        return sum + monthlyPayments.reduce((monthSum, payment) => monthSum + payment.amount, 0);
      }, 0);

      // Get active notices count
      const noticesSnapshot = await getDocs(
        query(collection(db, 'schools', currentSchoolId, 'notices'), where('published', '==', true))
      );
      const activeNotices = noticesSnapshot.size;

      return {
        totalStudents,
        totalTeachers,
        pendingAdmissions,
        totalFeeCollection,
        monthlyFeeCollection,
        pendingFees,
        activeNotices
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      return {
        totalStudents: 0,
        totalTeachers: 0,
        pendingAdmissions: 0,
        totalFeeCollection: 0,
        monthlyFeeCollection: 0,
        pendingFees: 0,
        activeNotices: 0
      };
    }
  },

  // Monthly fee data for specific school
  async getMonthlyFeeData(schoolId?: string): Promise<{ month: string; amount: number }[]> {
    const currentSchoolId = schoolId || await schoolDataApi.getCurrentSchoolId();
    if (!currentSchoolId) throw new Error('No school context found');

    try {
      const studentFeesSnapshot = await getDocs(collection(db, 'schools', currentSchoolId, 'studentFees'));
      const studentFees = studentFeesSnapshot.docs.map(doc => doc.data() as StudentFee);

      // Get last 12 months
      const months = [];
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({
          month: date.toLocaleDateString('en-US', { month: 'short' }),
          year: date.getFullYear(),
          monthIndex: date.getMonth()
        });
      }

      return months.map(({ month, year, monthIndex }) => {
        const monthlyAmount = studentFees.reduce((sum, fee) => {
          const monthlyPayments = fee.payments.filter(payment => {
            const paymentDate = new Date(payment.date);
            return paymentDate.getMonth() === monthIndex && paymentDate.getFullYear() === year;
          });
          return sum + monthlyPayments.reduce((monthSum, payment) => monthSum + payment.amount, 0);
        }, 0);

        return { month, amount: monthlyAmount };
      });
    } catch (error) {
      console.error('Error getting monthly fee data:', error);
      // Return dummy data in case of error to prevent UI crash
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
      return months.map(month => ({
        month,
        amount: Math.floor(Math.random() * 50000) + 10000
      }));
    }
  }
};